'use client'

import React, { forwardRef } from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon } from 'lucide-react'

// Tipos base do segmented control
type SegmentedControlSize = 'sm' | 'md' | 'lg'

// Interface para uma opção do segmented control
export interface SegmentedControlOption {
  value: string | number
  label: string
  icon?: LucideIcon
  disabled?: boolean
}

// Interface para aparência do segmented control
interface SegmentedControlAppearance {
  backgroundColor?: string
  selectedBackgroundColor?: string
  textColor?: string
  selectedTextColor?: string
  borderColor?: string
}

interface SegmentedControlProps {
  // Opções e valor
  options: SegmentedControlOption[]
  value?: string | number
  onChange?: (value: string | number) => void
  
  // Configuração visual
  size?: SegmentedControlSize
  
  // Estados
  disabled?: boolean
  
  // Labels
  label?: string
  helperText?: string
  
  // Customização
  appearance?: SegmentedControlAppearance
  
  // Layout
  fullWidth?: boolean
  className?: string
  
  // Comportamento
  required?: boolean
  
  // Propriedades HTML nativas
  name?: string
}

// Preset de aparência padrão
const defaultAppearance: SegmentedControlAppearance = {
  backgroundColor: colors.background.secondary,
  selectedBackgroundColor: colors.background.white,
  textColor: colors.text.secondary,
  selectedTextColor: colors.text.primary,
  borderColor: colors.border.primary,
}

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>((
  {
    options,
    value,
    onChange,
    size = 'md',
    disabled = false,
    label,
    helperText,
    appearance,
    fullWidth = false,
    className,
    required = false,
    name,
    ...props
  },
  ref
) => {
  // Determinar aparência final
  const finalAppearance: SegmentedControlAppearance = {
    backgroundColor: appearance?.backgroundColor || (disabled ? colors.palette.gray[50] : defaultAppearance.backgroundColor),
    selectedBackgroundColor: appearance?.selectedBackgroundColor || (disabled ? colors.palette.gray[100] : defaultAppearance.selectedBackgroundColor),
    textColor: appearance?.textColor || (disabled ? colors.palette.gray[400] : defaultAppearance.textColor),
    selectedTextColor: appearance?.selectedTextColor || (disabled ? colors.palette.gray[500] : defaultAppearance.selectedTextColor),
    borderColor: appearance?.borderColor || (disabled ? colors.palette.gray[200] : defaultAppearance.borderColor),
  }

  // Classes de tamanho
  const sizes = {
    sm: 'p-1 text-sm',
    md: 'p-1.5 text-base',
    lg: 'p-2 text-lg',
  }

  const optionSizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-5 py-2.5 text-lg',
  }

  // Handler de mudança
  const handleOptionClick = (optionValue: string | number, optionDisabled?: boolean) => {
    if (!disabled && !optionDisabled && onChange) {
      onChange(optionValue)
    }
  }

  // Classes do container
  const containerClasses = cn(
    'inline-flex rounded-lg border-2 transition-all duration-200',
    sizes[size],
    fullWidth && 'w-full',
    disabled && 'cursor-not-allowed',
    className
  )

  // Estilos do container
  const containerStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    borderColor: finalAppearance.borderColor,
  }

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full')}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium" style={{ color: finalAppearance.textColor }}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Segmented Control */}
      <div
        {...props}
        ref={ref}
        className={containerClasses}
        style={containerStyles}
        role="radiogroup"
        aria-label={label}
      >
        {options.map((option) => {
          const isSelected = option.value === value
          const isOptionDisabled = disabled || option.disabled
          const IconComponent = option.icon

          // Classes da opção
          const optionClasses = cn(
            'relative flex items-center justify-center gap-2 rounded-md font-medium transition-all duration-200 cursor-pointer select-none',
            optionSizes[size],
            fullWidth && 'flex-1',
            isSelected && 'shadow-sm',
            isOptionDisabled && 'cursor-not-allowed opacity-60',
            !isOptionDisabled && !isSelected && 'hover:bg-white/50'
          )

          // Estilos da opção
          const optionStyles: React.CSSProperties = {
            backgroundColor: isSelected ? finalAppearance.selectedBackgroundColor : 'transparent',
            color: isSelected ? finalAppearance.selectedTextColor : finalAppearance.textColor,
          }

          return (
            <div
              key={option.value}
              className={optionClasses}
              style={optionStyles}
              onClick={() => handleOptionClick(option.value, option.disabled)}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isOptionDisabled}
              tabIndex={isOptionDisabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleOptionClick(option.value, option.disabled)
                }
              }}
            >
              {IconComponent && (
                <IconComponent className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="truncate">{option.label}</span>
              
              {/* Hidden input para compatibilidade com formulários */}
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => {}} // Controlado pelo onClick do div
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={isOptionDisabled}
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>
          )
        })}
      </div>

      {/* Helper Text */}
      {helperText && (
        <div className="text-sm" style={{ color: colors.text.secondary }}>
          {helperText}
        </div>
      )}
    </div>
  )
})

SegmentedControl.displayName = 'SegmentedControl'